import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Pencil, Plus, RefreshCw, Repeat, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtEUR, fmtYm } from "@/lib/format";
import { ChannelBadge, ConfirmDialog, EmptyState, Hint, Loading, PageHeader, btnGhost, btnPrimary } from "@/components/app/common";
import { RicorrenteDialog } from "@/components/app/RicorrenteDialog";

function RicorrenteCard({ r, i, onEdit, onDelete, onToggle }) {
  return (
    <div data-testid={`ricorrente-card-${i}`} className={`card-soft p-5 flex flex-col gap-4 transition-opacity ${r.attiva ? "" : "opacity-60"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-lg leading-tight truncate">{r.nome}</h3>
          <div className="text-xs text-[#756B64] mt-1">{r.categoria}</div>
        </div>
        <Switch data-testid={`ricorrente-toggle-${i}`} checked={r.attiva} onCheckedChange={(v) => onToggle(r, v)} />
      </div>
      <div>
        <div className="text-2xl font-semibold tabular">{fmtEUR(r.imponibile)}</div>
        <div className="text-xs text-[#756B64] tabular">+ IVA {r.aliquota_iva}% = {fmtEUR(r.totale)} / mese</div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-[#5E4A42]">
        <ChannelBadge canale={r.canale} />
        <span className="px-2 py-0.5 rounded-full bg-[#F4EFEA]">ogni giorno {r.giorno}</span>
        <span className={`px-2 py-0.5 rounded-full ${r.attiva ? "bg-[#8AA090]/15 text-[#4E6656]" : "bg-[#EAE3DC] text-[#756B64]"}`} data-testid={`ricorrente-stato-${i}`}>{r.attiva ? "Attiva" : "In pausa"}</span>
      </div>
      <div className="text-xs text-[#756B64] space-y-1 border-t border-[#EAE3DC] pt-3">
        <div>Dal {fmtDate(r.data_inizio)}{r.data_fine ? ` al ${fmtDate(r.data_fine)}` : ""}</div>
        <div className="flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" />Prossimo addebito: <b className="text-[#2C2623] font-medium">{r.prossima_data ? fmtDate(r.prossima_data) : "—"}</b></div>
        <div>Ultimo mese generato: {fmtYm(r.ultimo_periodo)} · {r.periodi_generati.length} movimenti</div>
      </div>
      <div className="flex gap-2 justify-end">
        <button data-testid={`edit-ricorrente-${i}`} onClick={() => onEdit(r)} className="p-2 rounded-lg hover:bg-[#F4EFEA] text-[#756B64]"><Pencil className="h-4 w-4" /></button>
        <button data-testid={`delete-ricorrente-${i}`} onClick={() => onDelete(r)} className="p-2 rounded-lg hover:bg-[#E07A5F]/10 text-[#B4553D]"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export default function Ricorrenti() {
  const [items, setItems] = useState(null);
  const [categorie, setCategorie] = useState([]);
  const [edit, setEdit] = useState({ open: false, item: null });
  const [del, setDel] = useState(null);

  const load = useCallback(() => api.get("/ricorrenti").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); api.get("/categorie", { params: { tipo: "costo" } }).then((r) => setCategorie(r.data)); }, [load]);

  const toggle = async (r, attiva) => {
    try { await api.patch(`/ricorrenti/${r.id}/stato`, { attiva }); toast.success(attiva ? "Spesa ricorrente riattivata" : "Spesa ricorrente messa in pausa"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const genera = async () => {
    try { const { data } = await api.post("/ricorrenti/genera"); toast.success(data.generati ? `${data.generati} movimenti generati` : "Tutto aggiornato: nessun nuovo movimento da generare"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/ricorrenti/${del.id}`); toast.success("Spesa ricorrente eliminata"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };

  const totMese = (items || []).filter((r) => r.attiva).reduce((s, r) => s + r.imponibile, 0);

  return (
    <div data-testid="ricorrenti-page">
      <PageHeader overline="Costi fissi" title="Spese ricorrenti" subtitle={items ? `Totale mensile attivo: ${fmtEUR(totMese)} (imponibile)` : " "}>
        <Button variant="outline" className={btnGhost} onClick={genera} data-testid="genera-ricorrenti-button"><RefreshCw className="h-4 w-4 mr-1" />Genera ora</Button>
        <Button className={btnPrimary} onClick={() => setEdit({ open: true, item: null })} data-testid="add-ricorrente-button"><Plus className="h-4 w-4 mr-1" />Nuova spesa</Button>
      </PageHeader>
      <div className="mb-6"><Hint testId="ricorrenti-hint">Ogni mese, nel giorno indicato, viene creato automaticamente un movimento di costo (una sola volta per mese). Le modifiche all'importo valgono solo per i mesi futuri. Mettendo in pausa una spesa, i mesi di pausa non vengono generati.</Hint></div>
      {!items ? <Loading /> : !items.length ? (
        <EmptyState icon={Repeat} title="Nessuna spesa ricorrente" text="Aggiungi affitto, stipendi, abbonamenti software: verranno registrati in automatico ogni mese." action={<Button className={btnPrimary} onClick={() => setEdit({ open: true, item: null })}>Aggiungi la prima</Button>} />
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {items.map((r, i) => <RicorrenteCard key={r.id} r={r} i={i} onEdit={(x) => setEdit({ open: true, item: x })} onDelete={setDel} onToggle={toggle} />)}
        </div>
      )}
      <RicorrenteDialog open={edit.open} item={edit.item} categorie={categorie} onOpenChange={(o) => setEdit((s) => ({ ...s, open: o }))} onSaved={load} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Eliminare la spesa ricorrente?" text="I movimenti già generati resteranno nei Movimenti. Non verranno creati nuovi addebiti." onConfirm={remove} />
    </div>
  );
}
